import React, { useState, useRef } from "react";
import { TbUpload, TbFileCheck, TbX } from "react-icons/tb";
import { api } from "../../lib/api";
import { Button, Field, Alert, Modal } from "../../components";
import "./UploadScreen.css";

// Upload scan data, from inside the organization it belongs to.
//
// This used to be a whole screen reached from the sidebar, where the first thing it asked
// was which organization the file was for — a question you have already answered by being
// inside one. Here the organization is the context, so the dialog asks only what it
// cannot know: the file, and the key if the file is encrypted.
//
// Encryption is auto-detected the way the original does: the scanner writes an encrypted
// payload as a quoted string, so a file whose content starts with a double quote needs a
// key. That is a weak test, but changing it here would diverge from what the backend
// expects.
const UploadScanModal = ({ organizationId, organizationName, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const take = async (f) => {
    if (!f) return;
    setFile(f); setError("");
    try {
      const head = await f.slice(0, 1).text();
      setIsEncrypted(head.trim().startsWith('"'));
    } catch { setIsEncrypted(false); }
  };

  const clear = () => {
    setFile(null); setIsEncrypted(false); setKey("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (!file || !organizationId) return;
    setBusy(true); setError("");
    try {
      // FormData goes up without a Content-Type header. Setting one strips the multipart
      // boundary the browser generates and the upload fails silently.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("decryptionKey", key);
      fd.append("exportFormat", "docx");
      fd.append("organization_id", organizationId);
      await api.upload("s2/api/uploadfile", fd);
      // The new scan should be in the list behind the dialog before it closes, otherwise
      // the upload looks like it did nothing.
      await onUploaded();
      onClose();
    } catch (e) {
      setError(e.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Upload scan data${organizationName ? ` for ${organizationName}` : ""}`}
      onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" style={{ marginLeft: "auto" }}
                disabled={busy || !file || (isEncrypted && !key)}
                onClick={submit}>
          {busy ? "Uploading…" : "Upload and process"}
        </Button>
      </>}
    >
      <Alert>{error}</Alert>

      <div
        className={`v2-drop${dragging ? " on" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") inputRef.current?.click(); }}
      >
        <span className="v2-drop-icon"><TbUpload size={22} /></span>
        <span className="v2-drop-title">Drop the scan JSON here</span>
        <span className="v2-drop-hint">or <b>browse your files</b> · .json only</span>
        <input ref={inputRef} type="file" accept=".json,application/json" hidden
               onChange={(e) => take(e.target.files?.[0])} />
      </div>

      {file && (
        <div className="v2-filechip">
          <TbFileCheck size={15} color="var(--v2-success)" />
          <span className="v2-filechip-name">{file.name}</span>
          <span className="v2-filechip-size">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
          <Button icon aria-label="Remove file" onClick={clear}><TbX size={14} /></Button>
        </div>
      )}

      {isEncrypted && (
        <Field label="Decryption key"
               hint="This file is encrypted. The scanner encrypts its output when a key was set at scan time.">
          <input className="v2-input" type="password" value={key} autoFocus
                 onChange={(e) => setKey(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
};

export default UploadScanModal;
